import { useCallback, useEffect, useState, useRef } from 'react';
import { useLayoutEffect, useAppSelector, useAppDispatch } from '~app/hooks';
import { isAnimatingSelector, activeSelector, viewsSelector, setActive } from '~app/components/Survey/SurveyFormat';

export default function ActiveViewScrollManager(): null {
  const active = useAppSelector(activeSelector);
  const views = useAppSelector(viewsSelector);
  const isAnimating = useAppSelector(isAnimatingSelector);
  const dispatch = useAppDispatch();

  const [viewElements, setViewElements] = useState<HTMLElement[]>([]);

  useEffect(() => {
    const elements: HTMLElement[] = [];
    views.forEach(view => {
      const el = document.getElementById(`view-${view}`);
      if (el) {
        elements.push(el);
      }
    });
    setViewElements(elements);
  }, [views]);

  const findActiveAnchor = useCallback(() => {
    if (isAnimating) {
      return;
    }
    let newIdx = -1;
    if (window.scrollY === 0) {
      newIdx = 0;
    } else {
      const winMidpoint = window.innerHeight / 2;
      viewElements.forEach((el, idx) => {
        const rect = el.getBoundingClientRect();
        if (rect.top > 0 && rect.top <= winMidpoint) {
          newIdx = idx;
        }
      });
    }

    const currentAnchor = views[newIdx];

    if (currentAnchor && currentAnchor !== active) {
      dispatch(setActive(currentAnchor));
    }
  }, [isAnimating, views, active, viewElements, dispatch]);

  useLayoutEffect(() => {
    window.addEventListener('scroll', findActiveAnchor);
    return () => window.removeEventListener('scroll', findActiveAnchor);
  }, [findActiveAnchor]);

  // Initially focus on first active view
  // Only run on first render (prevents active state being stuck on top question when scrolled up on larger screens)
  const firstUpdate = useRef(true);
  useEffect(() => {
    if (firstUpdate.current) {
      findActiveAnchor();
      if (viewElements.length) {
        firstUpdate.current = false;
      }
    }
  }, [viewElements, findActiveAnchor]);

  return null;
}
